import { useEffect, useState } from "react";
import { Devices, ShieldWarning, MapPin, Clock, Power } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../ui/button";

/**
 * Sprint H · Batch 5 — Tarjeta "Mis sesiones activas".
 *
 * Lista cada login registrado contra el `token_version` actual.
 * El botón "Cerrar sesión en todos los dispositivos" bumpea `token_version`
 * → invalida atómicamente todas las cookies httpOnly emitidas.
 *
 * Props:
 *  - api:      instancia de axios autenticada (api o customerApi).
 *  - role:     "user" | "customer" → controla rutas y testid.
 *  - onLogoutAll: callback opcional tras cerrar todas las sesiones (para
 *                 redirigir al login y limpiar contexto).
 */
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function ActiveSessionsCard({ api, role = "user", onLogoutAll }) {
  const [sessions, setSessions] = useState(null);
  const [tv, setTv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const basePath = role === "customer" ? "/customer-auth" : "/auth";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${basePath}/sessions`);
      setSessions(data?.sessions || []);
      setTv(data?.token_version ?? null);
    } catch (e) {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const logoutAll = async () => {
    if (!window.confirm(
      "Vas a cerrar sesión en TODOS tus dispositivos (incluido el actual). " +
      "¿Continuar?",
    )) return;
    setBusy(true);
    try {
      await api.post(`${basePath}/logout-all-devices`);
      toast.success("Todas las sesiones cerradas. Te redirigimos al login…");
      if (onLogoutAll) onLogoutAll();
      else setTimeout(() => { window.location.href = role === "customer" ? "/cliente/login" : "/login"; }, 600);
    } catch (e) {
      toast.error("No se pudo cerrar todas las sesiones");
      setBusy(false);
    }
  };

  return (
    <div className="ag-card p-6" data-testid={`sessions-card-${role}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Devices weight="duotone" className="text-[var(--ag-aqua-soft)]" />
          <span className="font-semibold">Sesiones activas</span>
        </div>
        {tv != null && (
          <span className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] font-mono-tab">
            v{tv}
          </span>
        )}
      </div>

      <p className="text-xs text-[var(--ag-text-dim)] mb-4">
        Estos son los dispositivos donde has iniciado sesión recientemente. Si
        ves alguno desconocido, cierra todas las sesiones — invalidaremos
        atomicamente todas las cookies de autenticación.
      </p>

      {loading ? (
        <div className="text-xs text-[var(--ag-text-dim)] py-6 text-center">Cargando…</div>
      ) : !sessions || sessions.length === 0 ? (
        <div className="text-xs text-[var(--ag-text-dim)] py-6 text-center" data-testid={`sessions-empty-${role}`}>
          No hay sesiones registradas todavía. Cierra sesión y vuelve a entrar para
          empezar a rastrearlas.
        </div>
      ) : (
        <div className="space-y-2" data-testid={`sessions-list-${role}`}>
          {sessions.map((s, idx) => (
            <div key={s.id || idx} className="border border-[var(--ag-border)] rounded-md p-3 flex flex-wrap items-center gap-3"
                 data-testid={`session-${role}-${idx}`}>
              <div className="w-10 h-10 grid place-items-center bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded-md">
                <Devices size={18} weight="duotone" className="text-[var(--ag-aqua-soft)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" data-testid={`session-device-${role}-${idx}`}>
                  {s.device_label || `${s.browser || "Navegador"} · ${s.os || ""}`}
                </div>
                <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  {s.ip && (
                    <span className="inline-flex items-center gap-1 font-mono-tab">
                      <MapPin size={10} weight="bold" /> {s.ip}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <Clock size={10} weight="bold" /> {fmtDateTime(s.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 flex items-start gap-2 text-xs text-[var(--ag-text-dim)] p-3 bg-amber-500/10 border border-amber-500/30 rounded-md">
        <ShieldWarning size={16} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
        <div>
          Si pierdes un dispositivo o sospechas de actividad rara, cierra sesión
          en todos. Tendrás que volver a iniciar sesión donde quieras usar la app.
        </div>
      </div>

      <Button
        onClick={logoutAll}
        disabled={busy}
        variant="outline"
        className="mt-4 rounded-sm border-red-300 text-red-600 hover:bg-red-50"
        data-testid={`sessions-logout-all-${role}`}
      >
        <Power size={14} className="mr-1.5" weight="bold" />
        {busy ? "Cerrando…" : "Cerrar sesión en todos los dispositivos"}
      </Button>
    </div>
  );
}
